import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { isApiError, setRequestTenant } from '@/lib/http/http-api';
import { accessOptions } from '@/features/auth/access';
import { AppShell } from '@/components/app-shell';
import { PasswordSetupLayout } from '@/components/password-setup-layout';
import {
  loadCanManageUsers,
  requirePasswordChanged,
  requireSession,
} from '@/features/auth/session/guards';

/**
 * The boundary every signed-in page sits behind.
 *
 * `beforeLoad` awaits the session before anything renders and resolves the actor's
 * capabilities once, so the shell can hide what the API would refuse anyway. It returns
 * both into the route's context: children read `isAdmin` from there instead of asking
 * again, and the API stays the thing that actually enforces it.
 *
 * The password check sits between the two on purpose. An account that was handed an initial
 * password may only call the native better-auth routes (notably
 * `POST /api/auth/change-password`) until it has changed it; every business route answers
 * 403 `PASSWORD_CHANGE_REQUIRED`. Running the capability probe first would
 * read that 403 as "not an admin" and cache the wrong answer for five minutes, so the
 * redirect happens before it — and while the change is pending the probe is skipped
 * entirely (there is nothing to manage from a screen that only asks for a new password).
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context, location }) => {
    const session = await requireSession(context.queryClient, location.href);
    requirePasswordChanged(session, location.href, location.pathname);
    const organizationId = session.session.activeOrganizationId;
    if (setRequestTenant(organizationId ?? null)) {
      context.queryClient.clear();
      throw redirect({ to: '/tenants' });
    }
    if (
      !organizationId &&
      !['/tenants', '/set-password'].includes(location.pathname)
    ) {
      throw redirect({ to: '/tenants' });
    }
    if (
      session.user.mustChangePassword === true ||
      !organizationId ||
      location.pathname === '/tenants'
    ) {
      return { session, isAdmin: false, permissions: [] as string[] };
    }
    try {
      const isAdmin = await loadCanManageUsers(context.queryClient);
      const { permissions } =
        await context.queryClient.fetchQuery(accessOptions);
      return { session, isAdmin, permissions };
    } catch (error) {
      if (
        isApiError(error) &&
        ['TENANT_ACCESS_DENIED', 'TENANT_REQUIRED'].includes(error.code)
      ) {
        throw redirect({ to: '/tenants' });
      }
      throw error;
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { session } = Route.useRouteContext();
  if (session.user.mustChangePassword === true) {
    return (
      <PasswordSetupLayout>
        <Outlet />
      </PasswordSetupLayout>
    );
  }
  return (
    <AppShell menuEnabled={!!session.session.activeOrganizationId}>
      <Outlet />
    </AppShell>
  );
}
